import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

// FACTURATIE — centrale JOH Admin sectie (alleen zichtbaar binnen JOH Admin → Bedrijven).
// Facturen zijn NL, 21% btw, PROV-YYYY-NNN nummering, betalingstermijn 14/30 dagen.
// Financiële functies bestaan BEWUST niet in de PROVOCATIVE. Admin.

const eur = (c) => "€ " + ((c || 0) / 100).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATUS_LABEL = { draft: "CONCEPT", sent: "VERZONDEN", paid: "BETAALD", cancelled: "GEANNULEERD" };
const STATUS_COLOR = { draft: "#A3A3A3", sent: "#D4A93A", paid: "#7BAF7B", cancelled: "#666666" };
const inp = "editorial-input dark w-full";

export default function AdminCompanyBilling({ companyId, companyName }) {
  const [sender, setSender] = useState(null);
  const [profile, setProfile] = useState(null);
  const [extra, setExtra] = useState(""); // extra e-mails komma-gescheiden
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null); // factuur-modal (nieuw of concept)
  const [pay, setPay] = useState(null); // betalings-modal
  const [preview, setPreview] = useState(null); // preview-modal

  const load = () => {
    api.get("/companies/billing/sender").then(({ data }) => setSender(data.sender)).catch(() => toast.error("Afzender laden mislukt"));
    api.get(`/companies/${companyId}/billing/profile`).then(({ data }) => {
      setProfile(data.profile);
      setExtra((data.profile.extra_emails || []).join(", "));
    }).catch(() => toast.error("Facturatiegegevens laden mislukt"));
    api.get(`/companies/${companyId}/billing/invoices`).then(({ data }) => setData(data)).catch(() => toast.error("Facturen laden mislukt"));
  };
  useEffect(load, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!sender || !profile || !data) return <div className="chip text-[#4A4A4A]" data-testid="billing-loading">FACTURATIE …</div>;

  const saveSender = async () => {
    setBusy(true);
    try {
      await api.put("/companies/billing/sender", sender);
      toast.success("Afzender opgeslagen");
    } catch (err) { toast.error(err?.response?.data?.detail || "Opslaan mislukt"); }
    setBusy(false);
  };

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.put(`/companies/${companyId}/billing/profile`, {
        ...profile, payment_term_days: Number(profile.payment_term_days),
        extra_emails: extra.split(",").map((e) => e.trim()).filter(Boolean),
      });
      toast.success("Facturatiegegevens opgeslagen");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Opslaan mislukt"); }
    setBusy(false);
  };

  const openNew = () => setEdit({
    id: null,
    items: [{ description: "", qty: 1, unit_price: "" }],
    payment_term_days: profile.payment_term_days || 14,
    issue_date: new Date().toISOString().slice(0, 10),
    recipients: [profile.email, ...(profile.extra_emails || [])].filter(Boolean).join(", "),
    notes: "",
  });

  const openEdit = (inv) => setEdit({
    id: inv.id,
    items: inv.items.map((i) => ({ description: i.description, qty: i.qty, unit_price: (i.unit_price_cents / 100).toFixed(2) })),
    payment_term_days: inv.payment_term_days,
    issue_date: inv.issue_date,
    recipients: (inv.recipients || []).join(", "),
    notes: inv.notes || "",
  });

  const totals = (items) => {
    const sub = items.reduce((s, i) => s + Math.round((parseFloat(i.qty) || 0) * (parseFloat(i.unit_price) || 0) * 100), 0);
    const vat = Math.round(sub * 0.21);
    return { sub, vat, tot: sub + vat };
  };

  const saveInvoice = async () => {
    setBusy(true);
    try {
      const payload = {
        items: edit.items.map((i) => ({ description: i.description, qty: parseFloat(i.qty) || 0, unit_price: parseFloat(i.unit_price) || 0 })),
        payment_term_days: Number(edit.payment_term_days),
        recipients: edit.recipients.split(",").map((e) => e.trim()).filter(Boolean),
        notes: edit.notes,
      };
      if (edit.id) await api.patch(`/companies/${companyId}/billing/invoices/${edit.id}`, payload);
      else await api.post(`/companies/${companyId}/billing/invoices`, { ...payload, issue_date: edit.issue_date });
      toast.success("Factuur opgeslagen");
      setEdit(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Opslaan mislukt"); }
    setBusy(false);
  };

  const act = async (inv, action, label, body) => {
    try {
      await api.post(`/companies/${companyId}/billing/invoices/${inv.id}/${action}`, body || {});
      toast.success(label);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
  };

  const sendInvoice = async (inv) => {
    if (!window.confirm(`Factuur ${inv.number} versturen naar ${inv.recipients.join(", ")}? (PDF-bijlage, NL)`)) return;
    await act(inv, "send", `Factuur ${inv.number} verstuurd`);
  };

  const savePayment = async () => {
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/billing/invoices/${pay.id}/payments`, {
        amount: parseFloat(pay.amount) || 0, date: pay.date, note: pay.note,
      });
      toast.success("Betaling geregistreerd");
      setPay(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
    setBusy(false);
  };

  const mono = "chip";
  const s = data.summary;

  return (
    <div data-testid="billing-root">
      {/* ---- JOH afzender ---- */}
      <div className="grid lg:grid-cols-2 gap-10">
        <section data-testid="billing-sender">
          <div className={`${mono} text-[#4A4A4A] mb-4`}>JOH AFZENDERGEGEVENS (op elke factuur)</div>
          <div className="space-y-4">
            <input value={sender.name} onChange={(e) => setSender({ ...sender, name: e.target.value })} placeholder="NAAM *" className={inp} data-testid="sender-name" />
            <input value={sender.address} onChange={(e) => setSender({ ...sender, address: e.target.value })} placeholder="ADRES (straat + nr)" className={inp} data-testid="sender-address" />
            <input value={sender.zip_city} onChange={(e) => setSender({ ...sender, zip_city: e.target.value })} placeholder="POSTCODE + PLAATS" className={inp} data-testid="sender-zipcity" />
            <div className="grid grid-cols-2 gap-3">
              <input value={sender.kvk} onChange={(e) => setSender({ ...sender, kvk: e.target.value })} placeholder="KVK-NUMMER" className={inp} data-testid="sender-kvk" />
              <input value={sender.btw} onChange={(e) => setSender({ ...sender, btw: e.target.value })} placeholder="BTW-NUMMER" className={inp} data-testid="sender-btw" />
            </div>
            <input value={sender.iban} onChange={(e) => setSender({ ...sender, iban: e.target.value })} placeholder="IBAN" className={inp} data-testid="sender-iban" />
            <input value={sender.email} onChange={(e) => setSender({ ...sender, email: e.target.value })} placeholder="E-MAIL" className={inp} data-testid="sender-email" />
            <button onClick={saveSender} disabled={busy} className="btn-light disabled:opacity-40" data-testid="sender-save">{busy ? "…" : "OPSLAAN"}</button>
          </div>
        </section>

        {/* ---- bedrijfsprofiel ---- */}
        <section data-testid="billing-profile">
          <div className={`${mono} text-[#4A4A4A] mb-4`}>FACTURATIEGEGEVENS {companyName.toUpperCase()}</div>
          <div className="space-y-4">
            <input value={profile.billing_name} onChange={(e) => setProfile({ ...profile, billing_name: e.target.value })} placeholder="FACTUURNAAM BEDRIJF *" className={inp} data-testid="profile-name" />
            <input value={profile.attn} onChange={(e) => setProfile({ ...profile, attn: e.target.value })} placeholder="T.A.V. (CONTACTPERSOON)" className={inp} data-testid="profile-attn" />
            <input value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} placeholder="PRIMAIR FACTUUR-E-MAILADRES" className={inp} data-testid="profile-email" />
            <input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="EXTRA ONTVANGERS (komma-gescheiden)" className={inp} data-testid="profile-extra-emails" />
            <div className="grid grid-cols-2 gap-3">
              <input value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} placeholder="ADRES" className={inp} data-testid="profile-address" />
              <input value={profile.zip_city} onChange={(e) => setProfile({ ...profile, zip_city: e.target.value })} placeholder="POSTCODE + PLAATS" className={inp} data-testid="profile-zipcity" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input value={profile.kvk} onChange={(e) => setProfile({ ...profile, kvk: e.target.value })} placeholder="KVK" className={inp} data-testid="profile-kvk" />
              <input value={profile.btw} onChange={(e) => setProfile({ ...profile, btw: e.target.value })} placeholder="BTW-NUMMER" className={inp} data-testid="profile-btw" />
              <input value={profile.invoice_prefix} onChange={(e) => setProfile({ ...profile, invoice_prefix: e.target.value.toUpperCase() })} placeholder="PREFIX (PROV)" className={inp} data-testid="profile-prefix" />
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className={`${mono} text-[#A3A3A3]`}>BETALINGSTERMIJN</label>
              <select value={profile.payment_term_days} onChange={(e) => setProfile({ ...profile, payment_term_days: Number(e.target.value) })}
                className="editorial-input dark !w-auto" data-testid="profile-term">
                <option value={14}>14 DAGEN</option>
                <option value={30}>30 DAGEN</option>
              </select>
              <button onClick={saveProfile} disabled={busy} className="btn-light disabled:opacity-40 ml-auto" data-testid="profile-save">{busy ? "…" : "OPSLAAN"}</button>
            </div>
          </div>
        </section>
      </div>

      {/* ---- overzicht + acties ---- */}
      <div className="flex items-end justify-between mt-12 mb-4 flex-wrap gap-4">
        <div className="flex gap-8 flex-wrap" data-testid="billing-summary">
          <div><div className={`${mono} text-[#4A4A4A]`}>CONCEPTEN</div><div className="font-display text-2xl" data-testid="summary-drafts">{s.draft_count}</div></div>
          <div><div className={`${mono} text-[#4A4A4A]`}>OPENSTAAND</div><div className="font-display text-2xl" data-testid="summary-outstanding-count">{s.outstanding_count}</div></div>
          <div><div className={`${mono} text-[#4A4A4A]`}>OPENSTAAND TOTAAL</div><div className="font-display text-2xl" data-testid="summary-outstanding">{eur(s.outstanding_cents)}</div></div>
          <div><div className={`${mono} text-[#4A4A4A]`}>BETAALD TOTAAL</div><div className="font-display text-2xl" data-testid="summary-paid">{eur(s.paid_cents)}</div></div>
        </div>
        <div className="flex gap-3">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/billing/invoices.csv`}
            onClick={async (e) => {
              e.preventDefault();
              try {
                const { data: csv } = await api.get(`/companies/${companyId}/billing/invoices.csv`, { responseType: "text" });
                const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `facturen-${companyName}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              } catch { toast.error("CSV-export mislukt"); }
            }}
            className="btn-ghost-dark text-[#A3A3A3] border-[#F5F3F0]/25" data-testid="billing-csv">CSV EXPORT</a>
          <button onClick={openNew} className="btn-light" data-testid="billing-invoice-new">+ NIEUWE FACTUUR</button>
        </div>
      </div>

      {/* ---- facturenlijst ---- */}
      <div className="space-y-px border border-[#F5F3F0]/10" data-testid="billing-invoices">
        {data.items.length === 0 && <div className="px-4 py-3 text-sm text-[#4A4A4A]" data-testid="billing-empty">Nog geen facturen.</div>}
        {data.items.map((inv) => (
          <div key={inv.id} className="bg-[#0A0A0A] px-4 py-3" data-testid={`invoice-row-${inv.number}`}>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="font-mono text-sm" data-testid={`invoice-number-${inv.number}`}>{inv.number}</span>
              <span className={`${mono}`} style={{ color: STATUS_COLOR[inv.status] }} data-testid={`invoice-status-${inv.number}`}>
                {STATUS_LABEL[inv.status]}{inv.overdue ? " · VERVALLEN" : ""}
              </span>
              {inv.kind === "credit" && <span className={`${mono} text-[#D4A93A]`} data-testid={`invoice-kind-${inv.number}`}>CREDITNOTA → {inv.credit_for_number}</span>}
              {inv.credited && <span className={mono} style={{ color: "#666666" }} data-testid={`invoice-credited-${inv.number}`}>GECREDITEERD</span>}
              <span className="text-sm text-[#A3A3A3]">{inv.issue_date} → {inv.due_date}</span>
              <span className="font-mono text-sm ml-auto" data-testid={`invoice-total-${inv.number}`}>{eur(inv.total_cents)}</span>
              {inv.status === "sent" && <span className="text-xs text-[#D4A93A]" data-testid={`invoice-outstanding-${inv.number}`}>OPEN: {eur(inv.outstanding_cents)}</span>}
              {inv.status === "paid" && <span className="text-xs text-[#7BAF7B]">BETAALD: {eur(inv.amount_paid_cents)}</span>}
            </div>
            <div className="flex gap-4 mt-2 flex-wrap">
              <button onClick={() => setPreview(inv)} className={`${mono} text-[#A3A3A3] hover:text-[#F5F3F0] transition-colors`} data-testid={`invoice-preview-${inv.number}`}>PREVIEW</button>
              <button onClick={async () => {
                  try {
                    const { data: pdf } = await api.get(`/companies/${companyId}/billing/invoices/${inv.id}/pdf`, { responseType: "blob" });
                    const url = URL.createObjectURL(pdf);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `Factuur-${inv.number}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch { toast.error("PDF-download mislukt"); }
                }}
                className={`${mono} text-[#A3A3A3] hover:text-[#F5F3F0] transition-colors`} data-testid={`invoice-pdf-${inv.number}`}>PDF</button>
              {inv.status === "draft" && <button onClick={() => openEdit(inv)} className={`${mono} text-[#A3A3A3] hover:text-[#F5F3F0] transition-colors`} data-testid={`invoice-edit-${inv.number}`}>BEWERK</button>}
              {inv.status !== "cancelled" && inv.status !== "paid" && <button onClick={() => sendInvoice(inv)} className={`${mono} text-[#D4A93A] hover:text-[#F5F3F0] transition-colors`} data-testid={`invoice-send-${inv.number}`}>VERSTUUR</button>}
              {inv.status === "sent" && <button onClick={() => setPay({ id: inv.id, number: inv.number, amount: (inv.outstanding_cents / 100).toFixed(2), date: new Date().toISOString().slice(0, 10), note: "" })} className={`${mono} text-[#7BAF7B] hover:text-[#F5F3F0] transition-colors`} data-testid={`invoice-pay-${inv.number}`}>+ BETALING</button>}
              {inv.status !== "cancelled" && <button onClick={() => act(inv, "duplicate", "Gedupliceerd als concept")} className={`${mono} text-[#A3A3A3] hover:text-[#F5F3F0] transition-colors`} data-testid={`invoice-duplicate-${inv.number}`}>DUPLICEER</button>}
              {(inv.status === "sent" || inv.status === "paid") && inv.kind !== "credit" && !inv.credited && (
                <button onClick={() => window.confirm(`Creditnota aanmaken voor ${inv.number}? (negatief concept, bewerkbaar)`) && act(inv, "credit", "Creditnota aangemaakt als concept")} className={`${mono} text-[#D4A93A] hover:text-[#F5F3F0] transition-colors`} data-testid={`invoice-credit-${inv.number}`}>CREDITEER</button>
              )}
              {inv.status !== "cancelled" && inv.status !== "paid" && <button onClick={() => window.confirm(`Factuur ${inv.number} annuleren?`) && act(inv, "status", "Geannuleerd", { action: "cancel" })} className={`${mono} text-[#666666] hover:text-[#F5F3F0] transition-colors`} data-testid={`invoice-cancel-${inv.number}`}>ANNULEER</button>}
            </div>
          </div>
        ))}
      </div>

      {/* ---- factuur-modal (nieuw / bewerk concept) ---- */}
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="invoice-modal">
          <div className="absolute inset-0 bg-black/80" onClick={() => setEdit(null)} />
          <div className="relative bg-[#0A0A0A] border border-[#F5F3F0]/15 p-6 md:p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="font-display text-xl uppercase" data-testid="invoice-modal-title">{edit.id ? "Conceptfactuur bewerken" : "Nieuwe factuur"}</h2>
            <div className={`${mono} text-[#4A4A4A] mt-6 mb-2`}>FACTUURREGELS (21% btw wordt toegevoegd)</div>
            <div className="space-y-3" data-testid="invoice-items">
              {edit.items.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_80px_110px_24px] gap-2 items-center" data-testid={`invoice-item-${i}`}>
                  <input value={it.description} onChange={(e) => setEdit({ ...edit, items: edit.items.map((x, j) => j === i ? { ...x, description: e.target.value } : x) })}
                    placeholder="OMSCHRIJVING *" className={inp} data-testid={`invoice-item-${i}-desc`} />
                  <input value={it.qty} onChange={(e) => setEdit({ ...edit, items: edit.items.map((x, j) => j === i ? { ...x, qty: e.target.value } : x) })}
                    type="number" min="0" step="1" placeholder="AANTAL" className={inp} data-testid={`invoice-item-${i}-qty`} />
                  <input value={it.unit_price} onChange={(e) => setEdit({ ...edit, items: edit.items.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x) })}
                    type="number" min="0" step="0.01" placeholder="PRIJS €" className={inp} data-testid={`invoice-item-${i}-price`} />
                  <button onClick={() => setEdit({ ...edit, items: edit.items.filter((_, j) => j !== i) })} disabled={edit.items.length <= 1}
                    className={`${mono} text-[#A3A3A3] hover:text-[#F5F3F0] disabled:opacity-30 px-1`} data-testid={`invoice-item-${i}-remove`}>×</button>
                </div>
              ))}
            </div>
            <button onClick={() => setEdit({ ...edit, items: [...edit.items, { description: "", qty: 1, unit_price: "" }] })}
              className={`${mono} text-[#D4A93A] hover:text-[#F5F3F0] transition-colors mt-3`} data-testid="invoice-item-add">+ REGEL</button>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <div>
                <div className={`${mono} text-[#4A4A4A] mb-2`}>FACTUURDATUM</div>
                <input type="date" value={edit.issue_date} onChange={(e) => setEdit({ ...edit, issue_date: e.target.value })} disabled={!!edit.id}
                  className={`${inp} disabled:opacity-40`} data-testid="invoice-date" />
              </div>
              <div>
                <div className={`${mono} text-[#4A4A4A] mb-2`}>BETALINGSTERMIJN</div>
                <select value={edit.payment_term_days} onChange={(e) => setEdit({ ...edit, payment_term_days: Number(e.target.value) })} className={inp} data-testid="invoice-term">
                  <option value={14}>14 DAGEN</option>
                  <option value={30}>30 DAGEN</option>
                </select>
              </div>
            </div>
            <div className={`${mono} text-[#4A4A4A] mt-6 mb-2`}>ONTVANGERS (komma-gescheiden)</div>
            <input value={edit.recipients} onChange={(e) => setEdit({ ...edit, recipients: e.target.value })} placeholder="factuur@bedrijf.nl, …" className={inp} data-testid="invoice-recipients" />
            <div className={`${mono} text-[#4A4A4A] mt-4 mb-2`}>NOTITIES</div>
            <input value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} placeholder="INTERNE NOTITIE" className={inp} data-testid="invoice-notes" />
            {/* live totalen */}
            <div className="flex justify-end gap-8 mt-6 border-t border-[#F5F3F0]/10 pt-4" data-testid="invoice-totals">
              <span className={`${mono} text-[#4A4A4A]`}>SUBTOTAAL <span className="text-[#A3A3A3]">{eur(totals(edit.items).sub)}</span></span>
              <span className={`${mono} text-[#4A4A4A]`}>BTW 21% <span className="text-[#A3A3A3]">{eur(totals(edit.items).vat)}</span></span>
              <span className={`${mono} text-[#F5F3F0]`} data-testid="invoice-total-live">TOTAAL {eur(totals(edit.items).tot)}</span>
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={saveInvoice} disabled={busy} className="btn-light flex-1 justify-center disabled:opacity-40" data-testid="invoice-save">{busy ? "…" : "OPSLAAN ALS CONCEPT"}</button>
              <button onClick={() => setEdit(null)} className="btn-ghost-dark text-[#A3A3A3] border-[#F5F3F0]/25" data-testid="invoice-cancel">ANNULEREN</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- betalings-modal ---- */}
      {pay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="payment-modal">
          <div className="absolute inset-0 bg-black/80" onClick={() => setPay(null)} />
          <div className="relative bg-[#0A0A0A] border border-[#F5F3F0]/15 p-6 w-full max-w-sm">
            <h2 className="font-display text-xl uppercase">Betaling registreren</h2>
            <div className={`${mono} text-[#4A4A4A] mt-1`} data-testid="payment-number">{pay.number}</div>
            <div className="space-y-4 mt-6">
              <input value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} type="number" min="0" step="0.01" placeholder="BEDRAG €" className={inp} data-testid="payment-amount" />
              <input value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} type="date" className={inp} data-testid="payment-date" />
              <input value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} placeholder="NOTITIE (optioneel)" className={inp} data-testid="payment-note" />
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={savePayment} disabled={busy} className="btn-light flex-1 justify-center disabled:opacity-40" data-testid="payment-save">{busy ? "…" : "REGISTREREN"}</button>
              <button onClick={() => setPay(null)} className="btn-ghost-dark text-[#A3A3A3] border-[#F5F3F0]/25" data-testid="payment-cancel">ANNULEREN</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- preview-modal (HTML-weergave van de NL factuur) ---- */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="preview-modal">
          <div className="absolute inset-0 bg-black/80" onClick={() => setPreview(null)} />
          <div className="relative bg-white text-[#0A0A0A] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-[#0A0A0A] text-[#F5F3F0] px-8 py-5 flex items-center justify-between">
              <span className="font-display text-2xl">JOH.</span>
              <span className="font-display text-2xl">FACTUUR</span>
            </div>
            <div className="px-8 py-6 text-sm">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-[9px] tracking-[0.2em] text-[#858585] uppercase">Van</div>
                  <div className="mt-1 font-semibold">{sender.name}</div>
                  <div>{sender.address}</div>
                  <div>{sender.zip_city}</div>
                  <div>{sender.email}</div>
                </div>
                <div>
                  <div className="text-[9px] tracking-[0.2em] text-[#858585] uppercase">Aan</div>
                  <div className="mt-1 font-semibold">{profile.billing_name}</div>
                  {profile.attn && <div>t.a.v. {profile.attn}</div>}
                  <div>{profile.address}</div>
                  <div>{profile.zip_city}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-xs">
                <div><div className="text-[#858585] uppercase text-[9px] tracking-[0.2em]">Factuurnummer</div>{preview.number}</div>
                <div><div className="text-[#858585] uppercase text-[9px] tracking-[0.2em]">Factuurdatum</div>{preview.issue_date}</div>
                <div><div className="text-[#858585] uppercase text-[9px] tracking-[0.2em]">Vervaldatum</div>{preview.due_date}</div>
                <div><div className="text-[#858585] uppercase text-[9px] tracking-[0.2em]">Betalingstermijn</div>{preview.payment_term_days} dagen</div>
              </div>
              <table className="w-full mt-6 text-xs">
                <thead>
                  <tr className="bg-[#0A0A0A] text-[#F5F3F0]">
                    <th className="text-left px-3 py-2 uppercase tracking-wider text-[9px]">Omschrijving</th>
                    <th className="text-right px-3 py-2 uppercase tracking-wider text-[9px]">Aantal</th>
                    <th className="text-right px-3 py-2 uppercase tracking-wider text-[9px]">Prijs</th>
                    <th className="text-right px-3 py-2 uppercase tracking-wider text-[9px]">Bedrag</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((it, i) => (
                    <tr key={i} className="border-b border-[#E5E5E5]">
                      <td className="px-3 py-2">{it.description}</td>
                      <td className="px-3 py-2 text-right">{it.qty}</td>
                      <td className="px-3 py-2 text-right">{eur(it.unit_price_cents)}</td>
                      <td className="px-3 py-2 text-right">{eur(it.line_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 text-right text-xs space-y-1">
                <div className="text-[#858585]">SUBTOTAAL {eur(preview.subtotal_cents)}</div>
                <div className="text-[#858585]">BTW 21% {eur(preview.vat_cents)}</div>
                <div className="font-bold text-sm" data-testid="preview-total">TOTAAL {eur(preview.total_cents)}</div>
              </div>
              {sender.iban && (
                <div className="mt-6 text-[10px] text-[#858585]">
                  Gelieve het totaalbedrag binnen {preview.payment_term_days} dagen over te maken naar {sender.iban} t.n.v. {sender.name} o.v.v. factuurnummer {preview.number}.
                  {sender.kvk && ` KvK ${sender.kvk}.`}{sender.btw && ` BTW-nummer ${sender.btw}.`}
                </div>
              )}
            </div>
            <div className="px-8 pb-6 flex gap-3">
              <button onClick={() => setPreview(null)} className="btn-ghost-dark !text-[#0A0A0A] border-[#0A0A0A]/25" data-testid="preview-close">SLUITEN</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
